import React from 'react';
import './customer-list-card.scss';
import {
  ThreadListChannel,
  dispatch,
  getCatalogDownloadStatus,
  getSelectedItemIds,
  updateSelectedItemIds,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { SVGS } from '../../assets';
import { Avatar, SpinnerLoader } from '../../components';
import { useNetworkContext } from '../../network';
import { ReactComponent as ArrowRight } from '@zonofi/svg-icons/arrow_right.svg';
import { ReactComponent as Checkbox } from '@zonofi/svg-icons/checkbox.svg';
import { ReactComponent as Checked } from '@zonofi/svg-icons/checked.svg';
import { CustomerTagView } from '../customer-tag/customer-tag-view';
import { getFormattedAmount } from '@zonofi/common';
import moment from 'moment';
import { CustomerConversationTag } from '../customer-tag/customer-conversation-tag';
import { profileInFlightRequests } from '../../cache/in-flight-requests';

interface Customer {
  id: string;
  customerName: string;
  companyName: string;
  phone?: string;
  activity?: ThreadListChannel;
  conversation?: {
    conversationId?: string;
    tags?: string[];
    summary?: string;
    latestMessage?: string;
  };
  outstandingamount?: string;
  lastinvoicedate?: string;
  customerLifeCycleGroups?: any[];
  routes?: string[];
  segments?: string[];
  visits?: {
    checkInTime: string | null;
    checkOutTime: string | null;
    id: string | null;
    status: 'CO' | 'CI' | null;
  }[];
}

interface CustomerListCardProps {
  customer: Customer;
  onCheckboxToggle?: (customer: string) => void;
  onCustomerSelect?: (
    customerId: string,
    customerName: string,
    companyName: string,
    mobile?: string
  ) => void;
  isFirst?: boolean;
  isLast?: boolean;
  selectionMode?: boolean;
  selectedItem?: boolean;
  titleKey?: string;
  fallbackKeys?: string[];
  showCountryCode?: boolean;
  downloadStatus?: boolean;
  customerCardRef?: React.MutableRefObject<HTMLDivElement>;
}

export const CustomerListCard: React.FC<CustomerListCardProps> = ({
  customer,
  onCustomerSelect,
  onCheckboxToggle,
  isFirst = false,
  isLast = false,
  selectionMode = false,
  titleKey = '',
  fallbackKeys = [''],
  downloadStatus = false,
  customerCardRef = null,
}) => {
  const { tags = [] } = customer?.conversation || {};

  const getDisplayTitle = (data: any): string => {
    if (data[titleKey]) return data[titleKey];
    for (const key of fallbackKeys) {
      if (data[key]) return data[key];
    }

    return 'N/A';
  };

  const { networkStatus } = useNetworkContext();

  const selectedCustomerIds = useAppSelector(getSelectedItemIds);
  const customerCatalogDownloadStatus = useAppSelector(
    getCatalogDownloadStatus
  );

  const isSelected = selectedCustomerIds?.includes(customer?.id);

  return (
    <div
      ref={customerCardRef}
      className={`customer-list-card ${isFirst ? 'first-card' : ''} ${
        isLast ? 'last-card' : ''
      } `}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (selectionMode) {
          onCheckboxToggle?.(customer?.id);
        } else {
          onCustomerSelect?.(
            customer?.id,
            customer?.customerName || customer?.companyName,
            customer?.companyName,
            customer?.phone
          );
        }
      }}
    >
      {selectionMode &&
        (isSelected ||
        customerCatalogDownloadStatus?.[customer?.id]?.catalog ? (
          <Checked
            className="customer-list-card-checkbox"
            color={`${customerCatalogDownloadStatus?.[customer?.id]?.catalog ? '#f0f0f0' : '#000000'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              !customerCatalogDownloadStatus?.[customer?.id]?.catalog &&
                dispatch(updateSelectedItemIds(customer?.id));
            }}
          />
        ) : (
          <Checkbox
            className="customer-list-card-checkbox"
            color={'#8C8C8C'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(updateSelectedItemIds(customer?.id));
            }}
          />
        ))}
      <div className={`customer-list-card-content`}>
        <div className={`customer-list-card-content-avatar`}>
          <Avatar
            profileUrl=""
            id={customer?.id}
            title={getDisplayTitle(customer)}
            size={36}
            style={{ marginRight: '14px' }}
          />
        </div>

        <div className={`customer-details`}>
          <div>
            <div className="customer-details-card">
              <span className="customer-details-name font-semibold">
                {getDisplayTitle(customer)}
              </span>
              {/* <VisitTag status={customer?.visits?.[0]?.status ?? null} /> */}
            </div>

            {(customer?.conversation?.summary?.trim() ||
              customer?.activity?.latestMessage?.trim() ||
              customer?.conversation?.latestMessage?.trim()) && (
              <div className="customer-details-message font-regular">
                {customer?.conversation?.summary ||
                  customer?.activity?.latestMessage ||
                  customer?.conversation?.latestMessage}
              </div>
            )}

            {tags?.length > 0 && (
              <CustomerConversationTag conversationTags={tags} />
            )}

            <div className="customer-details-info font-regular">
              {`${
                customer?.outstandingamount &&
                !isNaN(Number(customer.outstandingamount))
                  ? `To Pay: ${
                      Number(customer.outstandingamount) > 0 ? 'DR ' : 'CR '
                    }${getFormattedAmount(
                      Math.abs(Number(customer.outstandingamount))
                    )}`
                  : ''
              }${
                customer?.lastinvoicedate
                  ? `${customer?.outstandingamount ? ' • ' : ''}Last Invoiced on ${moment(
                      customer.lastinvoicedate
                    ).format('DD/MM/YYYY')}`
                  : ''
              }`}
            </div>
            <CustomerTagView customer={customer} />
          </div>
          <div className="customer-details-actions">
            {customerCatalogDownloadStatus?.[customer.id]?.catalog && (
              <img src={SVGS.DownloadedIcon} />
            )}
            {profileInFlightRequests.has(customer.id) && networkStatus && (
              <SpinnerLoader color="#32A7E8" size={16} />
            )}
            <ArrowRight color="#8C8C8C" width={16} />
          </div>
        </div>
      </div>
    </div>
  );
};
